import axios from 'axios';
import { normalizeErrorMessage, parseApiError } from '../utils/errors';

const REFRESH_TOKEN_KEY = 'methaq_refresh_token';
const LOCALE_KEY = 'methaq_locale';

function getRequestLocale() {
  return localStorage.getItem(LOCALE_KEY) || 'ar';
}

let accessToken = null;
let refreshTokenValue = sessionStorage.getItem(REFRESH_TOKEN_KEY);
let onUnauthorized = null;
let isRefreshing = false;
let refreshQueue = [];

const client = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

export function getAccessToken() {
  return accessToken;
}

export function getRefreshToken() {
  return refreshTokenValue;
}

export function setTokens(access, refresh) {
  accessToken = access;
  refreshTokenValue = refresh;
  if (refresh) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } else {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function clearTokens() {
  accessToken = null;
  refreshTokenValue = null;
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function setOnUnauthorized(callback) {
  onUnauthorized = callback;
}

export function setAcceptLanguage(locale) {
  client.defaults.headers.common['Accept-Language'] = locale || 'ar';
}

function processQueue(error, token = null) {
  refreshQueue.forEach((promise) => {
    if (error) promise.reject(error);
    else promise.resolve(token);
  });
  refreshQueue = [];
}

client.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.headers['Accept-Language'] = getRequestLocale();
  config.headers['X-Request-Id'] = config.headers['X-Request-Id'] || `req_${Date.now()}`;
  return config;
});

client.interceptors.response.use(
  (response) => {
    if (response.config.responseType === 'blob') {
      return response.data;
    }
    const body = response.data;
    if (body && typeof body.success === 'boolean') {
      if (!body.success) {
        const err = new Error(normalizeErrorMessage(body.message) || 'Request failed');
        err.apiError = parseApiError({ response: { data: body, status: response.status } });
        throw err;
      }
      const payload = body.data;
      if (body.meta != null && payload != null && typeof payload === 'object' && !Array.isArray(payload)) {
        return { ...payload, meta: body.meta };
      }
      if (body.meta != null) {
        return { data: payload, meta: body.meta };
      }
      return payload;
    }
    return body;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    const isAuthLogin = requestUrl.includes('/auth/login');
    const isAuthRefresh = requestUrl.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthLogin && !isAuthRefresh) {
      if (!refreshTokenValue) {
        clearTokens();
        onUnauthorized?.();
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const { data } = await axios.post(
          `${client.defaults.baseURL}/auth/refresh`,
          { refreshToken: refreshTokenValue },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const payload = data.data || data;
        setTokens(payload.accessToken, payload.refreshToken || refreshTokenValue);
        processQueue(null, payload.accessToken);
        originalRequest.headers.Authorization = `Bearer ${payload.accessToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        onUnauthorized?.();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    const apiError = parseApiError(error);
    const err = new Error(apiError.message || 'Request failed');
    err.apiError = apiError;
    return Promise.reject(err);
  }
);

export default client;
